import Service, { type Registry as Services, service } from '@ember/service';
import { isTesting, macroCondition } from '@embroider/macros';
import {
  AnalyticsBrowser,
  type EventProperties as SegmentEventProperties,
  type MiddlewareFunction,
  type UserTraits,
} from '@segment/analytics-next';
import { PRODUCT_NAMES } from 'frontend-utils/utils/constants/constant-values';
import { dasherize } from 'frontend-utils/utils/convert-case';
import ENV from 'lumen/config/environment';
import {
  ROUTE_DICT,
  SHARED_ROUTES,
} from 'lumen/utils/constants/route-constants';
// FE-2182 Mixpanel session recording code, can be removed after trial
import mixpanel from 'mixpanel-browser';

export interface EventProperties extends SegmentEventProperties {
  eventTrigger: string;
}

export const TOOLTIP_HOVER_TIMEOUT = 3000;

// Percentages based on estimate of sessions we want to record for each product. See https://lighthouseintelligence.atlassian.net/browse/FE-2429
const MIXPANEL_SESSION_REPLAY_CONFIG = {
  [PRODUCT_NAMES.PRODUCT_BENCHMARK_INSIGHT]: {
    record_sessions_percent: 90,
  },
  [PRODUCT_NAMES.PRODUCT_REVENUE_INSIGHT]: {
    record_sessions_percent: 1,
  },
  [PRODUCT_NAMES.PRODUCT_RATE_INSIGHT]: {
    record_sessions_percent: 0.5,
  },
  [PRODUCT_NAMES.PRODUCT_PARITY_INSIGHT]: {
    record_sessions_percent: 4,
  },
  [PRODUCT_NAMES.PRODUCT_MARKET_INSIGHT]: {
    record_sessions_percent: 2,
  },
  [PRODUCT_NAMES.PRODUCT_PRICING_MANAGER]: {
    record_sessions_percent: 8,
  },
  [PRODUCT_NAMES.PRODUCT_SMART_DISTRIBUTION]: {
    record_sessions_percent: 100,
  },
  [PRODUCT_NAMES.PRODUCT_REVIEW_MANAGER]: {
    record_sessions_percent: 100,
  },
  [PRODUCT_NAMES.PRODUCT_SMART_INSIGHTS]: {
    record_sessions_percent: 1,
  },
};

const UserManagementStatus = [
  'inactive',
  'trial',
  'paying',
  'internal',
  'light',
] as const;

const UserManagementProducts = [
  'any',
  'benchmark_insight',
  'benchmark-insight',
  'benchmark-insight-upsell',
  'business-intelligence',
  'business-intelligence-upsell',
  'central-mapping',
  'channel-manager',
  'custom-reporting',
  'dmo',
  'market-insight',
  'market-insight-unsupported-market',
  'market-insight-upsell',
  'parity-insight',
  'pricing-manager',
  'rate-insight',
  'revenue-insight',
  'revenue-insight-upsell',
  'smart-distribution',
  'smart-insights',
  'spider',
  'total-revenue',
] as const;

export type UserManagementSubscriptionStatusType =
  (typeof UserManagementStatus)[number];
// @TODO: Confirm to remove 'benchmark_insight' from UserManagementProductTypes. See FE-2831
export type UserManagementProductTypes =
  (typeof UserManagementProducts)[number];

export type AvailableProductsTraitType = Partial<
  Record<UserManagementProductTypes, UserManagementSubscriptionStatusType>
>;

const mixpanelSessionReplayMiddleware: MiddlewareFunction = ({
  payload,
  next,
}): void => {
  if (payload.obj.type === 'track' || payload.obj.type === 'page') {
    if (mixpanel) {
      const segmentDeviceId = payload.obj.anonymousId;
      // Original ID Merge
      mixpanel.register({
        $device_id: segmentDeviceId,
        distinct_id: segmentDeviceId,
      });
      const sessionReplayProperties =
        mixpanel.get_session_recording_properties();
      payload.obj.properties = {
        ...payload.obj.properties,
        ...sessionReplayProperties,
      };
    }
  }

  if (payload.obj.type === 'identify') {
    if (mixpanel) {
      const { userId } = payload.obj;
      if (userId !== null) {
        mixpanel.identify(userId);
      }
    }
  }

  next(payload);
};

export default class UsageAnalyticsService extends Service {
  @service declare access: Services['access'];
  @service declare destination: Services['destination'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare riSelections: Services['ri-selections'];
  @service declare router: Services['router'];
  @service declare tab: Services['tab'];

  analytics: AnalyticsBrowser | null = null;
  // We can mock this property in the tests for this service.
  // This is better than setting `ENV.analytics.enabled` to true in the test environment,
  // because then we would do network requests to Segment in all of our tests.
  isEnabledInEnvironment: boolean = ENV.analytics.enabled;
  isLoggingEnabled: boolean = ENV.analytics.enableLogging;
  // `afterModel` can be called multiple times, e.g. when we do QP validation and adjust some QPs.
  // `initialise` doesn't have this issue because it's called in the `application` route.
  isSessionReplayInitialised = false;
  // The hotel and market routes identify on every `afterModel`, which runs more than once per switch,
  // so we skip repeats for the same hotel or market. The app-load identify has neither, so it always goes through.
  // Hotel and market ids can collide, so the key carries the context type, e.g. `subscription:123`.
  lastIdentifiedContextKey: string | undefined;

  get availableProductsTrait(): AvailableProductsTraitType {
    const products = {} as AvailableProductsTraitType;

    if (this.hotelAndMarketService.hotel?.productState) {
      Object.entries(this.hotelAndMarketService.hotel.productState).forEach(
        ([productKey, productValue]) => {
          // Temporary fix to handle the different product keys in the product state. This should be fixed by the API, but for now we add the checks here
          if (productKey === 'spider_analytics') {
            products['business-intelligence'] =
              productValue.subscription_status;
          } else if (productKey === 'pricing_assistant') {
            products['pricing-manager'] = productValue.subscription_status;
          } else {
            const productKeyDashCase = dasherize(
              productKey,
            ) as UserManagementProductTypes; // Send product name as dash case for consistency.
            products[productKeyDashCase] = productValue.subscription_status;

            if (
              !UserManagementProducts.includes(productKeyDashCase) ||
              !UserManagementStatus.includes(productValue.subscription_status)
            ) {
              this.errorTracking.captureException(
                `Unknown product or status in productState: ${productKeyDashCase}(${productKey}) - ${productValue.subscription_status}`,
              );
            }
          }
        },
      );
    }

    if (this.hotelAndMarketService.hotel?.benchmarkInsightIsLight) {
      const productKeyDashCase = dasherize(
        PRODUCT_NAMES.PRODUCT_BENCHMARK_INSIGHT,
      ) as UserManagementProductTypes;
      products[productKeyDashCase] = 'light';
    }

    return products;
  }

  get currentProductTrait(): string | null {
    const foundRoute =
      Object.keys(ROUTE_DICT).find((route) =>
        this.router.currentRouteName?.includes(route),
      ) ?? null;

    if (!foundRoute) {
      if (this.isLoggingEnabled) {
        // Log to the console if logging is enabled, so we can test the events in the browser during development.

        console.error(
          'No product found for route',
          this.router.currentRouteName,
        );
      }
      return null;
    }

    if (foundRoute === SHARED_ROUTES.PARITY_LIST) {
      return this.access.hasParityAnalytics
        ? PRODUCT_NAMES.PRODUCT_PARITY_INSIGHT
        : PRODUCT_NAMES.PRODUCT_RATE_INSIGHT;
    }

    const products = ROUTE_DICT[foundRoute as keyof typeof ROUTE_DICT];

    return products[0];
  }

  get formattedCurrentProductTrait(): UserManagementProductTypes | null {
    if (!this.currentProductTrait) {
      return null;
    }

    return dasherize(this.currentProductTrait) as UserManagementProductTypes;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get traitsGlobal() {
    const tabName = this.tab.activeTabName ?? '';
    const isStaffUser = Boolean(this.access.isStaffUser);
    const productTrait = this.formattedCurrentProductTrait;
    const additionalProductProperties =
      this.getAdditionalPropertiesForProduct(productTrait);
    const { userAgent } = navigator;
    // Pass all mobile devices
    // See https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#mobile_tablet_or_desktop
    const isMobileBrowser =
      userAgent.includes('Mobi') ||
      userAgent.includes('Android') ||
      userAgent.includes('iPhone') ||
      userAgent.includes('iPad');

    return {
      productSubscriptionStatus: productTrait
        ? this.availableProductsTrait[productTrait]
        : '',
      isStaffUser,
      userId: this.hotelAndMarketService.user?.id ?? '',
      isSingleHotelProduct: !this.hotelAndMarketService.market,
      ...Object.entries(this.availableProductsTrait).reduce(
        (subscriptionStatus, [product, status]) => {
          const capitalizedProduct = product
            .replace(/[-_]+(.)?/g, (match, char) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
              return char ? char.toUpperCase() : '';
            })
            .replace(/^./, (firstChar) => firstChar.toUpperCase());
          subscriptionStatus[`${capitalizedProduct}SubscriptionStatus`] =
            status;
          return subscriptionStatus;
        },
        {} as Record<string, string>,
      ),
      language: this.hotelAndMarketService.user?.locale ?? '',
      languageISO: this.hotelAndMarketService.user?.locale?.split('-')[0] ?? '',
      tabName,
      isMobileBrowser,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      ...additionalProductProperties,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get traitsShared() {
    return {
      // Mixpanel requires that we send some traits with every `track` & `page` event,
      // in order to correctly group and filter events.
      subscriptionId: this.hotelAndMarketService.hotel?.id ?? null,
      marketId: this.hotelAndMarketService.market?.id ?? null,
      destinationSubscriptionId:
        this.destination.destinationSubscription?.id ?? null,
      hotelinfoId: this.hotelAndMarketService.hotel?.hotelinfo?.id ?? null,
      product: this.formattedCurrentProductTrait,
      // Adding `hasSmartInsights` to all user events allows to easily filter Smart Insights session recordings in Mixpanel
      hasSmartInsights: this.access.hasSmartInsights,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get traitsTrack() {
    return {
      // Mixpanel requires that we send some traits with every `track` & `page` event,
      // in order to correctly group and filter events.
      ...this.traitsShared,
      route: this.router.currentRouteName,
      eventTrigger: null,
    };
  }

  /**
   * Some products require additional properties to be sent with the event.
   * This method returns those additional properties for a given product.
   * Note: not a great implementation, but was the quickest to achieve tight deadline & start collecting data asap
   * At least product specific code is grouped together so that it can be refactored more easily later on.
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getAdditionalPropertiesForProduct(product: string | null) {
    if (product === 'revenue-insight') {
      const properties: { tab?: string; view: string } = {
        view: this.riSelections.isSingleProperty
          ? 'single-property'
          : 'multi-property',
      };

      if (this.router.currentRouteName?.includes('hotel.revenue-insight')) {
        properties.tab =
          this.riSelections.selectedView === 'cancellations'
            ? 'cancellations'
            : 'bookings';
      }

      return properties;
    }

    if (product === 'total-revenue') {
      if (this.router.currentRouteName?.includes('market.total-revenue')) {
        return {
          view: this.router.currentRoute?.queryParams['view'] ?? 'departments',
        };
      }

      return { view: 'mapping' };
    }

    if (product === dasherize(PRODUCT_NAMES.PRODUCT_MARKET_INSIGHT)) {
      return {
        isMarketInsightLight: !this.access.isMarketInsightFull,
      };
    }

    if (product === 'pricing-manager' || product === 'smart-distribution') {
      const properties = {
        isAutopilotEnabled: Boolean(
          this.hotelAndMarketService.hotel?.paAutopilotSettings?.['enabled'],
        ),
        isPmsPricePushEnabled: this.access.hasFeature(
          'pricing_assistant.pms.push_integrated',
        ),
        isPmsPricePushEligible:
          this.access.hasFeature('pricing_assistant.pms.push_integrated') ||
          this.access.hasFeature('pricing_assistant.signup.open') ||
          this.access.hasFeature('pricing_assistant.signup.in_progress'),
      };

      return properties;
    }

    if (product === 'business-intelligence') {
      if (this.hotelAndMarketService.isMyHotels) {
        return { propertyView: 'my-hotels-view' };
      }

      if (this.hotelAndMarketService.market) {
        return { propertyView: 'market-view' };
      }

      return { propertyView: 'single-property-view' };
    }

    if (product === 'custom-currency') {
      return {
        view: this.hotelAndMarketService.hotel
          ? 'hotel-level'
          : 'organization-level',
      };
    }

    return {};
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getTrackContextTraits(properties: EventProperties) {
    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      subscriptionId:
        properties['subscriptionId'] || this.traitsTrack.subscriptionId,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      marketId: properties['marketId'] || this.traitsTrack.marketId,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      destinationSubscriptionId:
        properties['destinationSubscriptionId'] ||
        this.traitsTrack.destinationSubscriptionId,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      hotelinfoId: properties['hotelinfoId'] || this.traitsTrack.hotelinfoId,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      product: properties['product'] || this.traitsTrack.product,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      route: properties['route'] || this.traitsTrack.route,
      eventTrigger: properties['eventTrigger'],
    };
  }

  identify(userId: string, traits: UserTraits): void {
    if (!this.isEnabledInEnvironment) {
      return;
    }

    const { subscriptionId, marketId } = traits;
    let contextKey: string | undefined;
    if (subscriptionId !== undefined) {
      contextKey = `subscription:${String(subscriptionId)}`;
    } else if (marketId !== undefined) {
      contextKey = `market:${String(marketId)}`;
    }

    if (
      contextKey !== undefined &&
      contextKey === this.lastIdentifiedContextKey
    ) {
      return;
    }

    try {
      if (!this.analytics) {
        throw new Error(
          'usage-analytics is not initialised while calling identify',
        );
      }

      // userId is duplicated in the traits so we can add validation in the Segment Protocols.
      void this.analytics.identify(userId, {
        ...traits,
        userId,
        route: this.router.currentRouteName,
        products: Object.keys(this.availableProductsTrait),
      });

      this.lastIdentifiedContextKey = contextKey;
    } catch (error) {
      this.errorTracking.captureException(error, { userId });
    }
  }

  initialise(): void {
    if (macroCondition(!isTesting())) {
      document.body.addEventListener('mousedown', () => {
        // Notify the hotel and market service that the user is active
        this.hotelAndMarketService.ping();
      });
    }

    if (!this.isEnabledInEnvironment) {
      return;
    }

    try {
      this.analytics = AnalyticsBrowser.load(
        {
          writeKey: ENV.analytics.writeKey,
          cdnURL: ENV.analytics.cdnURL,
        },
        {
          integrations: {
            'Segment.io': {
              apiHost: ENV.analytics.apiHost,
              protocol: 'https',
            },
          },
          useQueryString: false,
        },
      );
    } catch (error) {
      this.errorTracking.captureException(error);
    }
  }

  /*
    The function is set up right now to be called on transitions to certain products.
    `product` can be passed to trigger a session replay for a specific product from e.g. a button click,
    overriding the current product trait. The product must be present in MIXPANEL_SESSION_REPLAY_CONFIG.
  */
  initialiseSessionReplay({
    product,
  }: { product?: keyof typeof MIXPANEL_SESSION_REPLAY_CONFIG } = {}): void {
    if (!this.isEnabledInEnvironment) {
      return;
    }

    // Don't record sessions for staff users.
    // This is to avoid an issue where the Intercom product tour preview window freezes due to Mixpanel session recording, which was blocking setting up product tours.
    if (this.access.isStaffUser) {
      return;
    }

    /* FE-2182 Start Mixpanel session recording code
     * can be removed after trial
     */
    try {
      if (!this.analytics) {
        this.errorTracking.addBreadcrumb({
          message:
            'Tried to initialize Mixpanel without Segment being initialized.',
        });
        return;
      }

      const productToUse = product ?? this.currentProductTrait;

      if (
        !this.isSessionReplayInitialised &&
        productToUse &&
        productToUse in MIXPANEL_SESSION_REPLAY_CONFIG
      ) {
        // Consider this done even if it fails, so we don't try to initialize it again.
        this.isSessionReplayInitialised = true;

        // https://docs.mixpanel.com/docs/session-replay/implement-session-replay/session-replay-web#init-options
        mixpanel.init(ENV['mixpanel-browser'].token, {
          api_host: ENV['mixpanel-browser'].apiHost,
          record_sessions_percent:
            MIXPANEL_SESSION_REPLAY_CONFIG[
              productToUse as keyof typeof MIXPANEL_SESSION_REPLAY_CONFIG
            ].record_sessions_percent,
          record_heatmap_data: true,
          record_mask_text_selector: '', // https://github.com/mixpanel/docs/blob/main/pages/docs/session-replay/session-replay-privacy-controls.mdx#configuring-privacy-controls
        });

        // Middleware to add Mixpanel's session recording properties to Segment events
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.analytics.addSourceMiddleware(mixpanelSessionReplayMiddleware);
      }
    } catch (error) {
      this.errorTracking.captureException(error);
    }
    /*
     * FE-2182 End Mixpanel session recording code
     */
  }

  page(): void {
    if (!this.isEnabledInEnvironment) {
      return;
    }

    try {
      if (!this.analytics) {
        return;
      }

      const { currentRouteName } = this.router;

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.analytics.page(
        { ...this.traitsShared, ...this.traitsGlobal, route: currentRouteName },
        {
          context: {
            screen: {
              width: screen.width,
              height: screen.height,
              density: window.devicePixelRatio,
            },
            traits: {
              ...this.traitsShared,
              route: currentRouteName,
            },
          },
        },
      );
    } catch (error) {
      this.errorTracking.captureException(error);
    }
  }

  track(event: string, properties: EventProperties): void {
    if (!this.isLoggingEnabled && !this.isEnabledInEnvironment) {
      return;
    }

    // Spread `eventProperties` after spreading `traitsTrack`, so that developers can overwrite the `product` trait by passing it in `properties`.
    const allProperties = {
      ...this.traitsTrack,
      ...this.traitsGlobal,
      ...properties,
    };

    if (this.isLoggingEnabled) {
      // Log to the console if logging is enabled, so we can test the events in the browser during development.

      console.info('Track', event, allProperties, {
        context: {
          screen: {
            width: screen.width,
            height: screen.height,
            density: window.devicePixelRatio,
          },
          traits: this.getTrackContextTraits(properties),
        },
      });
    }

    if (!this.isEnabledInEnvironment) {
      return;
    }

    try {
      if (!this.analytics) {
        throw new Error(
          'usage-analytics is not initialised while calling track',
        );
      }

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.analytics.track(event, allProperties, {
        context: {
          screen: {
            width: screen.width,
            height: screen.height,
            density: window.devicePixelRatio,
          },
          traits: this.getTrackContextTraits(properties),
        },
      });
    } catch (error) {
      this.errorTracking.captureException(error, { event, properties });
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'usage-analytics': UsageAnalyticsService;
  }
}
